<script lang="ts">
  import {
    createPlayer,
    type FoundationDestroy,
    type MediaConfig,
    PlayerEvent,
    type PlayerInterface,
    type RequestHeaderCallback,
  } from '@foundation-player/loader';
  import { onDestroy, onMount, setContext } from 'svelte';
  import Arrow from '../shared/assets/icons/Arrow.svelte';
  import Close from '../shared/assets/icons/Close.svelte';
  import Loading from '../shared/assets/icons/Loading.svelte';
  import IconButton from '../shared/components/IconButton.svelte';
  import { FocusManager } from '../shared/focus-manager/FocusManager';
  import { PlayerStore } from '../shared/player-store/PlayerStore';
  import type { PlayerLoaderConfig } from '../shared/types/player-loader-config';
  import type { PlayerStateContext } from '../shared/types/player-state';
  import { generateUniqueId } from '../shared/utils/generate-unique-id';
  import type { ShortsPlayerUI, ShortsPlayerUIConfig } from './types';
  import ControlsLayer from './ControlsLayer.svelte';
  import PosterImage from './PosterImage.svelte';
  import ErrorLayer from '../shared/components/ErrorLayer.svelte';
  import TextTrackLayer from '../shared/components/TextTrackLayer.svelte';

  /** The loader config to be used for the PlayerInterface. The UI won't need
   * it itself. */
  export let loaderConfig: PlayerLoaderConfig;

  /** An array of mediaConfigs to be shown and played within the Shorts Player. */
  export let mediaConfigs: MediaConfig[];

  /**
   * The index of the medium to start with.
   *
   * For example, if set to 3, the Shorts Player will initially show the fourth
   * video of the mediaConfigs array. When the fourth video ends, it will
   * automatically scroll to the fifth. During playout, the user can scroll up to
   * the third or down to the fifth.
   *
   * If the index is out of range, the Shorts Player will start with the first
   * medium.
   */
  export let uiConfig: ShortsPlayerUIConfig = {};

  /** The public API of the Shorts Player */
  export const api: Omit<ShortsPlayerUI, 'destroy'> = {
    /*
     * The integrator has to set the requestHeaderCallback, but they don't have
     * access to the player instance. So we offer a method on the ShortsPlayer
     * API to set the callback.
     * The integrator should set the requestHeaderCallback as soon as the
     * ShortsPlayer is created. However, at that time the player instance is not
     * yet created. So we have to wait until then.
     */
    setRequestHeaderCallback: (callback: RequestHeaderCallback) => {
      playerPromise.then((player) => player.setRequestHeaderCallback(callback));
    },
  };

  let resolvePlayerPromise: (player: PlayerInterface) => void;
  let rejectPlayerPromise: (error: unknown) => void;

  const playerPromise = new Promise<PlayerInterface>((resolve, reject) => {
    resolvePlayerPromise = resolve;
    rejectPlayerPromise = reject;
  });

  playerPromise.catch((error: Error) => {
    console.error('Player creation failed', error);
    showErrorLayer();
  });

  /**
   * Whether the device we're running on does not support touch.
   *
   * Scrolling with touch is easier than scrolling with a mouse or keyboard, so
   * we show buttons to navigate to the next video when the device does not
   * support touch.
   */
  const noTouchDevice = !('ontouchstart' in document);

  const { startIndex = 0 } = uiConfig;

  let closeButton: HTMLElement;
  let innerContainer: HTMLElement;
  let mediaContainer: HTMLElement;
  let playerElement: HTMLElement;
  let videoElement: HTMLVideoElement;
  let mediumElements: HTMLElement[] = [];
  let player: PlayerInterface | undefined;

  let intersectionObserver: IntersectionObserver;
  let resizeObserver: ResizeObserver;
  // initially, use the startIndex, but limit it to the available range
  let indexOfCurrentMedium = Math.min(Math.max(startIndex, 0), mediaConfigs.length - 1);
  let firstMediumActive = false;
  let lastMediumActive = false;
  let currentMediaConfig: MediaConfig = mediaConfigs[indexOfCurrentMedium];
  let offsetTopOfCurrentMedium = 0;

  /*
   * We use the FocusManager here, even though it was build for the Foundation
   * UI. We don't have all the layers and all the elements. However, we do have
   * some extra elements.
   * The clean solution would be to extend the FocusManager by some kind of
   * Generic (or a constructor parameter, or both).
   * The quick solution is to just use the FocusManager and happily re-use some
   * of the functionality.
   *
   * Elements of the ShortsPlayer that are not in the Foundation UI
   * - next video: mapped to seekForward
   * - previous video: mapped to seekBackward
   * - close dialog: mapped to close
   *
   * REFACTOR: Implement clean solution
   */
  const focusManager = new FocusManager();
  setContext<FocusManager>('focusManager', focusManager);

  let errorOccurred = false;

  /* Prevent duplicate ids as it would cause errors with multiple instances on one page */
  const idSuffix = generateUniqueId();
  const loadingIndicatorId = `loading-spinner-${idSuffix}`;
  const stallingIndicatorId = `stalling-spinner-${idSuffix}`;

  const showErrorLayer = () => {
    errorOccurred = true;
  };

  const hideErrorLayer = () => {
    errorOccurred = false;
  };

  const setLoading = () => {
    loading = true;
  };

  const playerStore = new PlayerStore();

  const { adSlotIsSkippable, sessionState, stalling } = playerStore;

  $: loading = $sessionState === 'loading';
  // TODO: Move to player store
  // We use the local variable instead of the sessionState because when moving
  // the player later (scrolling to next video), the sessionsState changes
  // (particularly when using the stus) quite some time after the player got
  // moved.
  $: waitingForPlayback = loading || $sessionState === 'not-yet-created' || $sessionState === 'ready';
  // We must also listen to loading as this locally overwrites the sessionState
  // for the UI at one point
  $: mediaLoaded = !loading && ($sessionState === 'active' || $sessionState === 'ready' || $sessionState === 'ended');
  $: scrollingDisabled = !$adSlotIsSkippable;

  $: firstMediumActive = indexOfCurrentMedium === 0;
  $: lastMediumActive = indexOfCurrentMedium === mediaConfigs.length - 1;

  $: {
    // When the medium was loaded, the play and mute buttons re-appear. Update
    // the focus manager. Timeout is needed, because otherwise this code would
    // run before the buttons are available.
    if (mediaLoaded) {
      updateFocus();
    }
  }

  setContext<PlayerStateContext>('playerState', playerStore);

  const scrollMedia = (direction: 'next' | 'previous' = 'next') => {
    if (scrollingDisabled) {
      return;
    }
    // Prevent scrolling if there is no more media to scroll to
    if ((direction === 'next' && lastMediumActive) || (direction === 'previous' && firstMediumActive)) {
      return;
    }
    const clientHeight = mediaContainer.getBoundingClientRect().height;
    const scrollAmount = direction === 'next' ? clientHeight : -clientHeight;

    mediaContainer.scrollBy({ top: scrollAmount, behavior: 'smooth' });
  };

  const onMediaMount = (event: CustomEvent<HTMLElement>) => {
    mediumElements.push(event.detail);
  };

  const scrollNextMediaOnSessionEnd = () => {
    scrollMedia('next');
  };

  const resetScrolling = () => {
    // we need to make sure that if ads can't be played (eg with
    // a bad network connection or an ad blocker) that the disabled
    // scrolling gets resetted as otherwise it wouldn't be
    // possible to load any other medium as there'd never be
    // an 'AdSlotCompleted' event
    // TODO: As we only force-stop the scrolling because of a
    // bug with v1 of the player, this can probably be removed
    // with v2 of the player (except the scrolling should still
    // be stopped immediately)
    scrollingDisabled = false;
  };

  const updateFocus = () => {
    setTimeout(() => {
      focusManager.updateFocusIfNeeded();
    }, 0);
  };

  onMount(async () => {
    // scroll to the start index (found in indexOfCurrentMedium)
    const currentMediumElement = mediumElements[indexOfCurrentMedium];
    const top = currentMediumElement.offsetTop;

    mediaContainer.scrollTo(0, top);

    // Important: we initialize the intersection observer first.
    // This way, the shorts ui (next/prev video) already works before the player
    // is initialized.
    intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const intersectingEntry = entries.find((entry) => entry.isIntersecting);

        if (intersectingEntry) {
          const attrValue = intersectingEntry.target.getAttribute('data-index');
          indexOfCurrentMedium = attrValue ? parseInt(attrValue) : -1;
          currentMediaConfig = mediaConfigs[indexOfCurrentMedium];

          if (errorOccurred && !player?.isDestroyed()) {
            hideErrorLayer();
          }

          // place the player and ui on top of the next element
          offsetTopOfCurrentMedium = (intersectingEntry.target as HTMLElement).offsetTop;

          // in the samsung browser the scrolling-stop can result in the video not being
          // "centered" but only partly visible
          // TODO: Remove with v2 of the player.
          mediaContainer.scrollTo({ top: offsetTopOfCurrentMedium, behavior: 'smooth' });

          // v1 of the player breaks when ads are playing and we have another
          // loadMedia-call. To prevent that we disable scrolling whenever
          // there's an ad defined (based on that we communicated that for v1 of
          // the player integrators got told to only use non-skippable ads).
          // Note: There can still be cases where scrolling past a medium is still
          // possible, for example when scrolling really fast. If that doesn't get
          // handled by the UI, the player would still break.
          // TODO: Remove with v2 of the player.
          if (currentMediaConfig.advertising?.slots.some((slot) => slot.offset === 0)) {
            scrollingDisabled = true;
          }
        }
      },
      {
        root: mediaContainer,
        threshold: 0.95,
      },
    );

    resizeObserver = new ResizeObserver(() => {
      const closeButtonClientRect = closeButton.getBoundingClientRect();
      const mediaContainerClientRect = mediaContainer.getBoundingClientRect();

      const overlapX = mediaContainerClientRect.right - closeButtonClientRect.left;
      const overlapY = closeButtonClientRect.bottom - mediaContainerClientRect.top;

      // 10px makes a better impression than 0
      const hasOverlap = overlapX > 10 && overlapY > 10;

      mediaContainer.style.setProperty('--close-button-overlap', `${hasOverlap ? Math.round(overlapY) : 0}px`);
    });

    resizeObserver.observe(innerContainer);

    mediumElements.forEach((element) => {
      intersectionObserver.observe(element);
    });

    try {
      // Added the shorts player identifier to the npaw config
      const npawConfig = loaderConfig.pluginConfigs?.['foundation.plugin.npawAnalytics'] || {};
      loaderConfig.pluginConfigs = {
        ...loaderConfig.pluginConfigs,
        'foundation.plugin.npawAnalytics': {
          ...npawConfig,
          shortsPlayer: true,
        },
      };

      player = await createPlayer({
        ...loaderConfig,
        playerElementOrId: playerElement,
        videoElementOrId: videoElement,
      });

      // When the video is paused we show a button on top of the video. When this
      // button gets activated the UI changes but the state does not. Therefore the 'afterUpdate' does
      // not get triggered and we have to manually trigger the updating.
      // TODO: Refactor that the focus gets updated automatically when it changes (by the
      // focus-manager), rather than manually updating the focus. Probably makes most sense after
      // having switched to Svelte 5 because lifecycles and related functionality changed.
      player.addEventListener(PlayerEvent.Playing, updateFocus);
      player.addEventListener(PlayerEvent.SessionEnd, scrollNextMediaOnSessionEnd);
      player.addEventListener(PlayerEvent.Destroy, onPlayerDestroy);
      player.addEventListener(PlayerEvent.ContentStart, resetScrolling);

      resolvePlayerPromise(player);

      playerStore.initializeStateFromPlayer(player);
    } catch (error) {
      rejectPlayerPromise(error);
    }
  });

  onDestroy(() => {
    intersectionObserver.disconnect();
    resizeObserver.disconnect();

    // release references to HTML elements
    mediumElements.length = 0;

    if (player?.isDestroyable()) {
      player.destroy('ShortsPlayer is destroyed');
    }
  });

  const onPlayerDestroy = (event: FoundationDestroy) => {
    if (event.error) {
      showErrorLayer();
    }
  };
</script>

<div class="shorts-player" class:no-touch-device={noTouchDevice} lang="de">
  <div class="inner-container" bind:this={innerContainer}>
    <div class="media-container" bind:this={mediaContainer} class:scrolling-disabled={scrollingDisabled}>
      {#each mediaConfigs as mediaConfig, index}
        <PosterImage {mediaConfig} {index} on:mount={onMediaMount} />
      {/each}

      <div
        class="video-ui"
        style:top="{offsetTopOfCurrentMedium}px"
        role="region"
        aria-label={currentMediaConfig.metadata?.headline
          ? `Shorts-Player ${currentMediaConfig.metadata.headline}`
          : 'Shorts-Player'}
        aria-busy={loading || $stalling}
        aria-describedby={loading ? loadingIndicatorId : $stalling ? stallingIndicatorId : undefined}
      >
        <div class="player-element" class:has-error={errorOccurred} bind:this={playerElement}>
          <!-- svelte-ignore a11y-media-has-caption -->
          <video aria-label={currentMediaConfig.metadata?.headline} disablepictureinpicture bind:this={videoElement} />
          {#if waitingForPlayback}
            {@const posterImageUrl = currentMediaConfig.metadata?.poster}
            <!-- With this the poster is shown when the player is loading the medium.
              Without it, the poster would be seen when scrolled to but then the player
              with the default black background and loading spinner would be seen until
              the first frame starts playing. -->
            <div class="poster" style:--poster-image={posterImageUrl ? `url(${posterImageUrl})` : undefined} />
          {/if}
        </div>

        {#if player}
          <div class="text-track-layer-wrapper">
            <TextTrackLayer {player} />
          </div>
        {/if}

        <ControlsLayer
          {player}
          {currentMediaConfig}
          {loading}
          {errorOccurred}
          on:errorCase={showErrorLayer}
          on:load={setLoading}
        />

        {#if errorOccurred}
          <ErrorLayer></ErrorLayer>
        {/if}

        {#if loading && !errorOccurred}
          <div class="loading-overlay">
            <Loading id={loadingIndicatorId} label="Video wird geladen" />
          </div>
        {/if}

        {#if $stalling}
          <div class="stalling-overlay">
            <Loading id={stallingIndicatorId} label="Video wird gepuffert" />
          </div>
        {/if}
      </div>
    </div>
    <div class="scroll-button-container">
      <div class="scroll-button scroll-button-previous">
        {#if !firstMediumActive}
          <IconButton
            on:click={() => scrollMedia('previous')}
            title="Vorheriges Video"
            focusName="seekBackward"
            focusLayer="content"
            disabled={scrollingDisabled}
          >
            <Arrow aria-hidden={true} />
          </IconButton>
        {/if}
      </div>
      <div class="scroll-button scroll-button-next">
        {#if !lastMediumActive}
          <IconButton
            on:click={() => scrollMedia('next')}
            title="Nächstes Video"
            focusName="seekForward"
            focusLayer="content"
            disabled={scrollingDisabled}
          >
            <Arrow aria-hidden={true} />
          </IconButton>
        {/if}
      </div>
    </div>

    <form method="dialog" class="close-button-container" bind:this={closeButton}>
      <IconButton aria-label="Schließen" type="submit" focusName="close" focusLayer="content">
        <Close ariaHidden="true" />
      </IconButton>
    </form>
  </div>
</div>

<style module>
  /* This is the root element of the shorts player. It acts as a CSS container,
   * so we are able to resize the videos depending on the available space. */
  .shorts-player {
    --primary-color: #f0f0f0;
    --accent-color: #ffffff;
    --disabled-color: #424447;
    --advertisement-color: #f1bf42;
    --focus-background: #000000;
    --focus-color: #ffffff;

    --controls-contrast-background: rgba(24, 24, 24, 0.75);
    --controls-no-transparency-background: rgb(24, 24, 24);
    --controls-gradient: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.5) 20%, rgba(0, 0, 0, 0) 50%);

    --menu-background-color: #000000;
    --menu-border-color: #5a5a5a;
    --menu-item-hover-background-color: rgba(255, 255, 255, 0.2);

    --icon-color: var(--primary-color);
    --icon-background-color: transparent;
    --icon-focus-color: var(--focus-color);
    --icon-focus-background-color: var(--focus-background);
    --icon-hover-color: var(--focus-color);
    --icon-hover-background-color: var(--focus-background);
    --icon-disabled-color: var(--disabled-color);
    --icon-disabled-background-color: var(--icon-background-color);
    --icon-contrast-outline: var(--outline);

    --slider-bar-color: rgba(255, 255, 255, 0.5);
    --slider-track-color: var(--primary-color);
    --slider-knob-color: var(--primary-color);
    --slider-hover-bar-color: var(--slider-bar-color);
    --slider-hover-track-color: var(--accent-color);
    --slider-hover-knob-color: var(--accent-color);

    --outline: 2px solid var(--accent-color);

    --loading-spinner-max-size: 52px;

    --poster-image-background-color: #666;

    --error-layer-background-color: transparent;

    --tooltip-background: #000000;
    --tooltip-color: #ffffff;

    container-name: shortsplayer;
    container-type: size;

    width: 100%;
    height: 100%;

    color: var(--primary-color, #f5f5f5);
  }

  /* We need this inner container to apply a padding when the shorts player is
   * larger. We cannot apply the padding on the shorts player element itself,
   * because CSS does not allow to style a container based on its own
   * dimensions. */
  .inner-container {
    position: relative;

    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  /* This is the element that contains the poster images of all media, one below
   * the other. Exactly one poster image is visible. The user can scroll this
   * container to move to the next or previous videos. It also includes the UI
   * that we move around according to the currently scrolled-to medium. */
  .media-container {
    position: relative;
    aspect-ratio: 9/16;
    max-height: 100%;
    max-width: 100%;
    /* When the aspect-ratio is below 9/16, the width is the limiting factor for
     * the size of the video and poster images. */
    width: 100%;
    height: auto;

    display: flex;
    flex-direction: column;
    overflow: auto;
    scroll-snap-type: block mandatory;
    scroll-snap-stop: always;
    scrollbar-width: none;
  }

  .media-container.scrolling-disabled {
    overflow: hidden;
  }

  @container (min-aspect-ratio: 9/16) {
    .media-container {
      /* When the aspect-ratio is 9/16 or higher, the height becomes the
       * limiting factor for the size of the video and poster images. */
      height: 100%;
      width: auto;
    }
  }

  .loading-overlay,
  .stalling-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .scroll-button-container {
    width: 44px;
    height: 100px;
    display: none;

    position: absolute;
    right: 24px;
  }

  .scroll-button {
    position: absolute;
    --icon-background-color: #292929;
    --icon-border-radius: 50%;
  }

  .scroll-button-previous {
    top: 0;
    transform: rotate(90deg);
  }

  .scroll-button-next {
    bottom: 0;
    transform: rotate(270deg);
  }

  .close-button-container {
    position: absolute;
    top: 10px;
    right: 10px;

    /* We hide the close button container (the form to close the dialog) ... */
    display: none;
  }

  /* ...unless it lies within a dialog element. */
  :global(dialog) .close-button-container {
    display: block;
  }

  /* The element that contains the video element, the controls and the loading
   * indicator. */
  .video-ui {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    container-name: player-wrapper;
    container-type: inline-size;
  }

  .text-track-layer-wrapper {
    position: absolute;
    left: 0;
    bottom: 10%;
    width: 100%;

    display: flex;
    padding: 0 64px;

    box-sizing: border-box;
  }

  /* This is the player element needed by the Foundation Player */
  .player-element {
    /* overwrite the 260px set by bitmovin */
    min-width: 0 !important;

    /* force the background color to black, needed if the video is not 9/16 */
    background-color: black !important;
  }

  .player-element > video {
    width: 100%;
    height: 100%;
  }

  .player-element.has-error {
    /* hide the player to make the poster image visible */
    visibility: hidden;
  }

  .poster {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-color: var(--poster-image-background-color, #666);
    background-image: var(--poster-image, none);
  }

  @container (min-aspect-ratio: 1) {
    .no-touch-device .scroll-button-container {
      display: block;
    }

    .inner-container {
      padding-block: 48px;
      box-sizing: border-box;
    }

    .close-button-container {
      top: 48px;
      right: 24px;

      --icon-background-color: #292929;
      --icon-border-radius: 50%;
    }
  }
</style>
